import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import AuthenticatorSetup from "../component/AuthenticatorSetup";
import { UNSET_ADMIN } from "../store/admin/types";
import { authApi } from "../util/adminAuthApi";
import { warningv2 } from "../util/Alert";
import { Toast } from "../util/Toast";

// Re-authentication fields required by every credential change: the current
// password plus an authenticator code. A lost authenticator is handled by
// another admin's "Reset sign-in".
const StepUpFields = ({ value, onChange, idPrefix }) => (
  <div className="row">
    <div className="col-md-6 mb-3">
      <label className="form-label" htmlFor={`${idPrefix}-current`}>
        Current password
      </label>
      <input
        id={`${idPrefix}-current`}
        type="password"
        className="form-control"
        autoComplete="current-password"
        value={value.currentPassword}
        onChange={(e) => onChange({ ...value, currentPassword: e.target.value })}
      />
    </div>
    <div className="col-md-6 mb-3">
      <label className="form-label" htmlFor={`${idPrefix}-code`}>
        Authenticator code
      </label>
      <input
        id={`${idPrefix}-code`}
        className="form-control"
        autoComplete="one-time-code"
        inputMode="numeric"
        value={value.code}
        onChange={(e) => onChange({ ...value, code: e.target.value })}
      />
    </div>
  </div>
);
const emptyStepUp = { currentPassword: "", code: "" };
const stepUpPayload = (value) => ({
  currentPassword: value.currentPassword,
  code: value.code.replace(/\s/g, ""),
});

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

const Heading = () => (
  <div className="page-heading">
    <div className="page-title">
      <div className="row">
        <div className="col-12 col-md-6 order-md-1 order-last">
          <h3 className="mb-3 text-white">Security</h3>
        </div>
        <div className="col-12 col-md-6 order-md-2 order-first">
          <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/admin/dashboard" className="text-danger">
                  Dashboard
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Security
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
);

const Security = () => {
  const dispatch = useDispatch();
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);

  const [passwordStepUp, setPasswordStepUp] = useState(emptyStepUp);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [mfaStepUp, setMfaStepUp] = useState(emptyStepUp);
  const [mfaChange, setMfaChange] = useState(null);
  const [mfaCode, setMfaCode] = useState("");

  const signOut = (message) => {
    if (message) Toast("success", message);
    dispatch({ type: UNSET_ADMIN });
    window.location.href = "/login";
  };

  const loadSessions = useCallback(async () => {
    const result = await authApi.sessions().catch(() => null);
    if (result?.status) setSessions(result.sessions);
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      Toast("error", "Unable to reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const changePassword = (event) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) return Toast("error", "The new passwords do not match.");
    run(async () => {
      const result = await authApi.changePassword({
        ...stepUpPayload(passwordStepUp),
        newPassword,
      });
      if (!result.status) return Toast("error", result.message);
      signOut(result.message);
    });
  };

  const startMfaChange = (event) => {
    event.preventDefault();
    run(async () => {
      const result = await authApi.startMfaChange(stepUpPayload(mfaStepUp));
      if (!result.status) return Toast("error", result.message);
      setMfaStepUp(emptyStepUp);
      setMfaChange(result);
    });
  };

  const completeMfaChange = (event) => {
    event.preventDefault();
    run(async () => {
      const result = await authApi.completeMfaChange(mfaChange.ticket, mfaCode.replace(/\s/g, ""));
      setMfaCode("");
      if (!result.status) return Toast("error", result.message);
      setMfaChange(null);
      // The server ended every session; sign in again with the new authenticator.
      signOut(result.message);
    });
  };

  const revokeSession = (session) =>
    run(async () => {
      const result = await authApi.revokeSession(session._id);
      if (!result.status) return Toast("error", result.message);
      if (session.current) return signOut("Signed out.");
      Toast("success", result.message);
      loadSessions();
    });

  const revokeAll = () =>
    warningv2(
      "Sign out everywhere?",
      "Every session, including this one, ends immediately. Use this if you suspect your account was used by someone else."
    ).then((confirmed) => {
      if (!confirmed) return;
      run(async () => {
        const result = await authApi.revokeAllSessions();
        if (!result.status) return Toast("error", result.message);
        signOut(result.message);
      });
    });

  return (
    <>
      <Heading />
      <div className="card">
        <div className="card-header">
          <h4 className="card-title mt-3">Change password</h4>
        </div>
        <div className="card-body">
          <form onSubmit={changePassword} autoComplete="off">
            <StepUpFields value={passwordStepUp} onChange={setPasswordStepUp} idPrefix="pw" />
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="new-password">
                  New password (at least 12 characters)
                </label>
                <input
                  id="new-password"
                  type="password"
                  className="form-control"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="confirm-password">
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  className="form-control"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>
            <p className="text-muted small">Changing your password signs out every session.</p>
            <button className="btn btn-danger" type="submit" disabled={busy}>
              Change password
            </button>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h4 className="card-title mt-3">Authenticator</h4>
        </div>
        <div className="card-body">
          {!mfaChange ? (
            <form onSubmit={startMfaChange} autoComplete="off">
              <p className="text-muted">
                Move sign-in to a new phone or authenticator app. Your old authenticator stops
                working. If you have lost your phone, ask another admin to reset your sign-in.
              </p>
              <StepUpFields value={mfaStepUp} onChange={setMfaStepUp} idPrefix="mfa" />
              <button className="btn btn-danger" type="submit" disabled={busy}>
                Replace authenticator
              </button>
            </form>
          ) : (
            <form onSubmit={completeMfaChange} autoComplete="off">
              <AuthenticatorSetup setup={mfaChange.setup} />
              <div className="mb-3 mx-auto" style={{ maxWidth: 260 }}>
                <label className="form-label" htmlFor="new-mfa-code">
                  Code from the new authenticator
                </label>
                <input
                  id="new-mfa-code"
                  className="form-control"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                />
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <button className="btn btn-danger" type="submit" disabled={busy}>
                  Confirm new authenticator
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => setMfaChange(null)}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mt-3">Active sessions</h4>
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={revokeAll} disabled={busy}>
            Sign out everywhere
          </button>
        </div>
        <div className="card-body table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Signed in</th>
                <th>Last active</th>
                <th>Expires</th>
                <th>IP</th>
                <th>Device</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session._id}>
                  <td>
                    {formatDate(session.createdAt)}
                    {session.current && <span className="badge bg-success ms-2">This session</span>}
                  </td>
                  <td>{formatDate(session.lastSeenAt)}</td>
                  <td>{formatDate(session.expiresAt)}</td>
                  <td>{session.ip || "—"}</td>
                  <td className="small" style={{ maxWidth: 260, wordBreak: "break-word" }}>
                    {session.userAgent || "—"}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0"
                      onClick={() => revokeSession(session)}
                    >
                      Sign out
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Security;
